import { GridItem, Grid } from "@chakra-ui/react";
import { withoutNullable } from "@syla/shared/types/helpers/withoutNullable";
import { uniqBy, uniqueId } from "lodash";
import React, { useMemo, Fragment, useEffect } from "react";
import { SetOptional } from "type-fest";
import { useCurrentAccountStore } from "../../../../../../store/currentAccountStore";
import { useTransactionsStore } from "../../../../../../store/transactionsStore";
import {
  useQueryAccountAssetOptions,
  formatAssetOption,
} from "../../../../../../store/useQueryAccountAssetOptions";
import { useQueryAssetOptions } from "../../../../../../store/useQueryAssets";
import { OrderFilterSetting } from "../../../../../../types/order/orderQuery";
import { StandardSelectOption } from "../../../../../selector/parts/SelectOption";
import { SingleSelector } from "../../../../../selector/SingleSelector";
import { maxAssetsToDisplay } from "../../../ledgerInputConstants";
import { setOperation, SwitchAssetOperationEntry } from "../common";
import { ArrowGridItem } from "../components/ArrowGridItem";
import { DeleteButtonGridItem } from "../components/DeleteButtonGridItem";

export function AssetBulkEdit({
  filters,
  operations,
  selectedIds,
  setOperations,
}: {
  operations: SetOptional<SwitchAssetOperationEntry, "from" | "to">[];
  setOperations: React.Dispatch<
    React.SetStateAction<
      SetOptional<SwitchAssetOperationEntry, "from" | "to">[]
    >
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
}) {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const { data: allAssetOptionsResult } = useQueryAssetOptions();
  const allAssetOptions = allAssetOptionsResult ?? [];

  const groups = useTransactionsStore((s) => s.groups);

  const selectedAssetOptions = useMemo(() => {
    if (!groups || selectedIds.length === 0) {
      return undefined;
    }

    return uniqBy(
      withoutNullable(
        selectedIds.flatMap((gid) =>
          groups[gid].data.transactions.flatMap((transaction) =>
            transaction.ledgers.flatMap((ledger) => ledger.asset),
          ),
        ),
      ),
      (asset) => asset._id,
    ).map(formatAssetOption);
  }, [groups, selectedIds]);

  const { data: accountAssetOptions, isLoading: accountAssetsLoading } =
    useQueryAccountAssetOptions(accountId, { enabled: !selectedAssetOptions });

  const filteredAndSelectedAssetOptions = useMemo(
    () =>
      (selectedAssetOptions ?? []).filter((option) =>
        filters.asset.includes(option.value),
      ),
    [selectedAssetOptions, filters.asset],
  );

  // prepopulate operations based on selected types or filters
  useEffect(() => {
    if (
      selectedAssetOptions !== undefined &&
      selectedAssetOptions.length === 1
    ) {
      setOperations(
        selectedAssetOptions.map((option) => ({
          id: uniqueId(),
          type: "asset",
          from: option.value,
        })),
      );
    } else if (
      filteredAndSelectedAssetOptions.length > 0 &&
      filteredAndSelectedAssetOptions.length <= 3
    ) {
      setOperations(
        filteredAndSelectedAssetOptions.map((option) => ({
          id: uniqueId(),
          type: "asset",
          from: option.value,
        })),
      );
    } else if (
      selectedAssetOptions === undefined &&
      filters.asset.length > 0 &&
      filters.asset.length <= 3
    ) {
      setOperations(
        filters.asset.map((option) => ({
          id: uniqueId(),
          type: "asset",
          from: option,
        })),
      );
    } else {
      setOperations([{ id: uniqueId(), type: "asset" }]);
    }
  }, [
    filteredAndSelectedAssetOptions,
    selectedAssetOptions,
    filters.asset,
    setOperations,
  ]);

  return (
    <Grid
      w="full"
      templateColumns={{ base: "1fr", sm: "1fr auto 1fr auto" }}
      alignItems="center"
      justifyItems="flex-start"
      columnGap={4}
      rowGap={{ base: 2, sm: 4 }}
    >
      <Fragment>
        {operations.map((op) => (
          <Fragment key={op.id}>
            <AssetOperation
              key={op.id}
              operation={op}
              fromAssetOptions={
                selectedAssetOptions ?? accountAssetOptions ?? []
              }
              fromOptionsLoading={accountAssetsLoading}
              toAssetOptions={allAssetOptions}
              toOptionsLoading={!allAssetOptionsResult}
              setOperation={setOperation(op, setOperations)}
            />
            {operations.length > 1 && (
              <DeleteButtonGridItem
                operation={op}
                operations={operations}
                setOperations={setOperations}
              />
            )}
          </Fragment>
        ))}
      </Fragment>
    </Grid>
  );
}

function AssetOperation({
  operation,
  setOperation,
  fromAssetOptions,
  fromOptionsLoading,
  toAssetOptions,
  toOptionsLoading,
}: {
  operation: SwitchAssetOperationEntry;
  setOperation: React.Dispatch<React.SetStateAction<SwitchAssetOperationEntry>>;
  fromAssetOptions: StandardSelectOption[];
  fromOptionsLoading: boolean;
  toAssetOptions: StandardSelectOption[];
  toOptionsLoading: boolean;
}): JSX.Element {
  return (
    <>
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
          value={operation.from}
          onChange={(value) => {
            setOperation((operation) => ({
              ...operation,
              from: value,
            }));
          }}
          options={fromAssetOptions}
          isLoading={fromOptionsLoading}
          maxOptionsDisplayed={maxAssetsToDisplay}
          placeholder="Select Asset"
          enableSearch
          autoSelectSingleItem
        />
      </GridItem>
      <ArrowGridItem />
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
          value={operation.to}
          onChange={(value) => {
            setOperation((operation) => ({
              ...operation,
              to: value,
            }));
          }}
          options={toAssetOptions
            // don't show the same value
            .filter(({ value }) => value != operation.from)}
          maxOptionsDisplayed={maxAssetsToDisplay}
          placeholder="Select Asset"
          enableSearch
          isLoading={toOptionsLoading}
        />
      </GridItem>
    </>
  );
}
